import _ from 'lodash';
import { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown } from 'react-bootstrap';
import Gallery from 'react-photo-gallery';
import Dropzone from 'react-dropzone';
import Queue from 'promise-queue';
import path from 'path';
import JsZip from 'jszip';
import FileSaver from 'file-saver';
import BlazeCamera from '@dsisolutions/blaze-camera';
import DetectRTC from 'detectrtc';
import classNames from 'classnames';

import LoadingPage from '../LoadingPage';
import config from '../../config';
import ModalContext from './ModalContext';
import ModalStatusBar from './ModalStatusBar';
import FileList from '../form/misc/FileList';
import SelectedImage from './gallery/SelectedImage';
import ApiService from '../../services/ApiService';
import { modal } from './ModalEmitter';
import { bytesToSize } from '../../util/form';
import { simpleHash } from '../../util/general';
import { useLocalStorage } from '../../util/hooks';
import GoogleImageSearch from '../GoogleImageSearch';
import BottomPanel from '../attachments/BottomPanel';
import Tags from '../attachments/Tags';
import toastr from '../toastr';
import '../../styles/attachments.scss';
import { isFileImage } from '../../util/fileTypeImages';

const getSearchQuery = ({ itemId, itemData }) => {
  let terms = [itemId.replace(/.:/g, '')];

  if (itemData.Make || itemData.Model) {
    terms = terms.concat([itemData.Year, itemData.Make, itemData.Model]);
  }

  if (itemData.Description) {
    terms.push(itemData.Description);
  }

  return terms.filter(x => !!x).join(' ');
};

const ModalAttachments = ({ itemName, itemId, itemData = {}, readOnly = false, onChange }) => {
  const [attachments, setAttachments] = useState([]);
  const [fetchingTags, setFetchingTags] = useState(true);
  const [tags, setTags] = useState([]);
  const [onlyTags, setOnlyTags] = useState([]);
  const [onlyPublic, setOnlyPublic] = useState(false);
  const [onlyImages, setOnlyImages] = useState(false);
  const [onlyDocs, setOnlyDocs] = useState(false);

  // Selection Mode
  const [selection, setSelection] = useState(false);

  // Top Shade Toggle
  const [showTopShade, setShowTopShade] = useLocalStorage('showAttachmentTopShade', false);

  // Attachment target to appear in bottom panel
  const [detailTarget, setDetailTarget] = useState(null);

  // Gallery / List Views
  const [view, setView] = useState('Gallery');
  const [loading, setLoading] = useState(true);
  const [cameraMode, setCameraMode] = useState(false);

  const [progressMap, setProgressMap] = useState({});

  const { setTitle } = useContext(ModalContext);

  const dropzoneEl = useRef(null);
  const statusbarEl = useRef(null);

  const defaultStatusMessage = () => {
    if (loading) {
      return 'Loading...';
    }

    if (onlyTags.length) {
      const filtered = compileAttachments();
      if (filtered.length !== attachments.length) {
        return `${filtered.length} of ${attachments.length} attachment${attachments.length !== 1 ? 's' : ''}`;
      }
    }

    return `${attachments.length} attachment${attachments.length !== 1 ? 's' : ''}`;
  };

  const download = url => {
    return fetch(url).then(res => res.blob());
  };

  const getExtFromMime = mime => {
    switch (mime) {
      case 'image/jpeg':
        return 'jpg';

      default:
        return mime.split('/')[1];
    }
  };

  const exportZip = files => {
    const zip = JsZip();

    files.forEach(({ blob, attachment }) => {
      zip.file(`${attachment.filename}-${attachment.id}.${getExtFromMime(attachment.mimeType)}`, blob);
    });

    zip.generateAsync({ type: 'blob' }).then(zipFile => {
      const currentDate = new Date().getTime();
      const fileName = `${itemName}-${itemId}-${currentDate}.zip`;
      return FileSaver.saveAs(zipFile, fileName);
    });
  };

  const downloadSelected = () => {
    const selected = attachments.filter(x => x.selected);

    const downloadQueue = new Queue(5, Infinity);
    const promises = [];

    for (const attachment of selected) {
      promises.push(
        downloadQueue.add(async () => {
          const blob = await download(`${config.serverUrl}/api/attachment/${attachment.key}/download`);
          return {
            blob,
            attachment,
          };
        })
      );
    }

    Promise.all(promises).then(exportZip);
  };

  const refreshList = (skipLoading = false) => {
    if (!skipLoading) {
      setLoading(true);
    }

    ApiService.get(`list/${itemName}/item/${encodeURIComponent(itemId)}/attachments`)
      .then(response => {
        setAttachments(response.data || []);
        if (statusbarEl.current) {
          statusbarEl.current.setMessage(defaultStatusMessage());
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchTags = () => {
    setFetchingTags(true);
    return ApiService.get('tags')
      .then(response => {
        setTags(response.data);
      })
      .finally(() => {
        setFetchingTags(false);
      });
  };

  const filterTag = tagId => {
    if (onlyTags.includes(Number(tagId))) {
      return;
    }

    setOnlyTags(prev => [...prev, Number(tagId)]);
  };

  const addTagToSelected = selectedTag => {
    const tagId = Number(selectedTag);
    const attachmentIds = attachments.filter(x => x.selected).map(att => att.id);
    const tag = tags.find(t => t.id === tagId);

    if (!tag || !attachmentIds.length) {
      return;
    }

    return ApiService.put(`/tag/${tagId}/batch-add`, { attachmentIds }).then(() => {
      toastr.success(
        'Tag Added',
        `Tag ${tag.label} added to ${attachmentIds.length} attachment${attachmentIds.length > 1 ? 's' : ''}`
      );

      setAttachments(current => {
        return current.map(att => {
          if (attachmentIds.includes(att.id)) {
            if (!att.tags.find(t => t.tagId === tagId)) {
              return { ...att, tags: [...att.tags, { tagId, attachmentId: att.id }] };
            }
          }

          return att;
        });
      });
    });
  };

  const removeTagFromSelected = selectedTag => {
    const tagId = Number(selectedTag);
    const attachmentIds = attachments.filter(x => x.selected).map(att => att.id);
    const tag = tags.find(t => t.id === tagId);

    if (!tag || !attachmentIds.length) {
      return;
    }

    return ApiService.post(`/tag/${tagId}/batch-remove`, { attachmentIds }).then(() => {
      toastr.success(
        'Tag Removed',
        `Tag ${tag.label} removed from ${attachmentIds.length} attachment${attachmentIds.length > 1 ? 's' : ''}`
      );

      setAttachments(current => {
        return current.map(att => {
          if (attachmentIds.includes(att.id)) {
            return { ...att, tags: att.tags.filter(t => t.tagId !== tagId) };
          }

          return att;
        });
      });
    });
  };

  const updateTags = (attachmentId, newTags = []) => {
    setAttachments(
      attachments.map(attachment => {
        if (attachment.id === attachmentId) {
          attachment.tags = newTags;
          return attachment;
        }

        return attachment;
      })
    );
  };

  const addAttachments = files => {
    return ApiService.post(
      `list/${encodeURIComponent(itemName)}/item/${encodeURIComponent(itemId)}/attachment`,
      files
    ).then(response => {
      const responseFiles = response.data || [];

      setAttachments(atts => {
        return atts.map(att => {
          const rsFile = responseFiles.find(rf => rf.filename === att.filename);

          if (rsFile) {
            return {
              ...rsFile,
              uploading: false,
              src: `${config.serverUrl}/api/attachment/${rsFile.key}`,
              placeholder: att.src, // probably cached blob
            };
          }

          return att;
        });
      });
    });
  };

  const handleImageSearchSelection = images => {
    modal.close();

    if (!images || !images.length) {
      return;
    }

    // Loading temp thumbnails while they are being added as attachments
    setAttachments([
      ...attachments,
      ...images.map((image, index) => ({
        width: image.width,
        height: image.height,
        mimeType: image.mime,
        src: image.thumbnail,
        key: `new-${index}`,
        uploading: true,
      })),
    ]);

    ApiService.put('attachment', [{ data: images }], {
      onUploadProgress: p => {
        setProgressMap(c => {
          const prog = parseInt((p.loaded / p.total) * 100);
          const imgs = {};
          images.forEach(img => (imgs[simpleHash(img.thumbnail)] = prog));
          return { ...c, ...imgs };
        });
      },
    }).then(response => {
      let failed = false;
      const files = response.data.filter(x => {
        if (x.failed) {
          failed = true;
        }
        return !x.failed;
      });

      if (failed) {
        toastr.error('Upload Failed', 'At least one file failed to upload');
      }

      addAttachments(files).then(() => {
        refreshList(true);
      });
    });
  };

  const handleScroll = () => {
    // const container = this.dropzone.node;
    // if (container.scrollHeight === container.scrollTop + container.offsetHeight) {
    //   // load moar?
    // }
  };

  const handleTopShadeToggle = () => {
    setShowTopShade(prev => !prev);
  };

  const handleDetailTarget = attachmentId => {
    if (attachmentId === detailTarget) {
      setDetailTarget(null);
    } else {
      setDetailTarget(attachmentId);
    }
  };

  const remove = (file = {}) => {
    // Using key because the id is enumerated
    const attachmentId = file.key;

    if (!attachmentId) {
      console.warn('No attachmentId was given');
      return;
    }

    modal.confirm({
      message: 'Remove attachment?',
      callback: () => {
        ApiService.delete(`list/${itemName}/item/${encodeURIComponent(itemId)}/attachment/${attachmentId}`).then(() => {
          refreshList(true);
        });
      },
    });
  };

  const select = (event, attachment) => {
    event.stopPropagation();
    const checkmark = event.checkmark;

    if (view === 'Gallery' && (checkmark || selection)) {
      const newAttachments = attachments.map((meta, index) => {
        if (index !== attachment.index) {
          return meta;
        }

        return { ...meta, selected: !meta.selected };
      });

      setAttachments(newAttachments);
    }
  };

  const selectAttachment = attachmentId => {
    setAttachments(current => {
      return current.map(att => {
        if (att.id === attachmentId) {
          return { ...att, selected: !att.selected };
        }

        return att;
      });
    });
  };

  const openAttachment = attachment => {
    // TODO Lightbox Open/Download
    if (attachment.key) {
      window.open(`${config.serverUrl}/api/attachment/${attachment.key}/download`);
    } else {
      window.open(`${attachment.src}/download`, '_new');
    }
  };

  const unSelectAll = () => {
    setAttachments(
      attachments.map(attachment => {
        return { ...attachment, selected: false };
      })
    );
    setSelection(false);
  };

  const isAllSelected = () => {
    for (const attachment of attachments) {
      if (!attachment.selected) {
        return false;
      }
    }
    return true;
  };

  const selectAll = () => {
    const newAttachments = attachments.map(attachment => {
      if (!onlyTags.length) {
        return { ...attachment, selected: true };
      }

      for (const tag of onlyTags) {
        if (attachment.tags && attachment.tags.find(t => t.tagId === tag)) {
          return { ...attachment, selected: true };
        }
      }

      return { ...attachment, selected: false };
    });

    setAttachments(newAttachments);
    setSelection(newAttachments);
  };

  const selectedCount = () => {
    return attachments.filter(attachment => attachment.selected).length;
  };

  const linkSelected = () => {
    const attachmentIds = [];

    attachments.forEach(attachment => {
      if (attachment.selected) {
        attachmentIds.push(attachment.id);
      }
    });

    modal.open('linkAttachment', { attachmentIds, itemName, unSelectAll });
  };

  const removeSelected = () => {
    const attachmentIds = [];

    attachments.forEach(attachment => {
      if (attachment.selected) {
        attachmentIds.push(attachment.id);
      }
    });

    modal.confirm({
      message: `Remove ${attachmentIds.length} attachment${attachmentIds.length !== 1 ? 's' : ''}?`,
      callback: () => {
        ApiService.post(`list/${itemName}/item/${encodeURIComponent(itemId)}/attachments/remove`, {
          attachmentIds,
        }).then(() => {
          setAttachments(
            attachments.filter(attachment => {
              return _.indexOf(attachmentIds, attachment.id) === -1;
            })
          );
          setSelection(false);
        });
      },
    });
  };

  const publicSelected = () => {
    const attachmentIds = [];

    attachments.forEach(attachment => {
      if (attachment.selected) {
        attachmentIds.push(attachment.id);
      }
    });

    return ApiService.post('attachments/batch-public', { attachmentIds }).then(() => {
      setSelection(false);
      setAttachments(current => {
        return current.map(att => {
          if (attachmentIds.includes(att.id)) {
            return { ...att, isPublic: true, selected: false };
          }

          return att;
        });
      });
    });
  };

  const privateSelected = () => {
    const attachmentIds = [];

    attachments.forEach(attachment => {
      if (attachment.selected) {
        attachmentIds.push(attachment.id);
      }
    });

    return ApiService.post('attachments/batch-private', { attachmentIds }).then(() => {
      setSelection(false);
      setAttachments(current => {
        return current.map(att => {
          if (attachmentIds.includes(att.id)) {
            return { ...att, isPublic: false, selected: false };
          }

          return att;
        });
      });
    });
  };

  const onDrop = uploadList => {
    const uploadQueue = new Queue(2, 10000);
    const indexes = {};
    const jobs = [];
    const files = [];

    uploadList.forEach(file => {
      const formData = new FormData();

      const current = files.push({
        filename: file.name,
        originalname: file.name,
        size: file.size,
        uploading: true,
        src: file.preview,
        width: 4,
        height: 3,
        mimeType: file.type,
        key: file.name,
      });

      indexes[`${file.name}${file.size}`] = current - 1;
      formData.set('files', file, file.name);

      if (progressMap[file.name]) {
        setProgressMap(c => ({ ...c, [simpleHash(file.name)]: 0 }));
      }

      jobs.push(
        uploadQueue
          .add(() => {
            return ApiService.put('upload', formData, {
              onUploadProgress: p => {
                setProgressMap(c => ({ ...c, [simpleHash(file.name)]: parseInt((p.loaded / p.total) * 100) }));
              },
            });
          })
          .then(response => {
            const fileData = response.data.files[0];
            const { originalname, size } = fileData;
            files[indexes[`${originalname}${size}`]] = fileData;
            files[indexes[`${originalname}${size}`]].uploading = false;

            ApiService.put('attachment', [{ data: [files[indexes[`${originalname}${size}`]]] }]).then(response => {
              addAttachments(response.data);
            });
          })
      );
    });

    setAttachments([...attachments, ...files]);
  };

  const compileAttachments = () => {
    const onMouseOut = () => {
      if (statusbarEl.current) {
        statusbarEl.current.setMessage(defaultStatusMessage());
      }
    };

    const filtered = attachments.filter(attachment => {
      if (onlyPublic && !attachment.isPublic) {
        return false;
      }

      if (onlyImages && !isFileImage(attachment)) {
        return false;
      }

      if (onlyDocs && isFileImage(attachment)) {
        return false;
      }

      if (!onlyTags.length) {
        return true;
      }

      for (const tag of onlyTags) {
        if (attachment.tags && attachment.tags.find(t => t.tagId === tag)) {
          return true;
        }
      }

      return false;
    });

    return filtered.map(attachment => {
      const src =
        attachment.uploading || !attachment.id
          ? attachment.src
          : `${config.serverUrl}/api/attachment/${attachment.key}`;

      return {
        ...attachment,
        src,
        onMouseOver: () => {
          if (statusbarEl.current) {
            statusbarEl.current.setMessage(`${attachment.filename} (${bytesToSize(attachment.size)})`);
          }
        },
        onMouseOut,
      };
    });
  };

  const reorder = files => {
    const payload = files.map(f => f.id);

    ApiService.put(`list/${itemName}/item/${encodeURIComponent(itemId)}/attachments`, payload).then(response => {
      setAttachments(response.data);
    });
  };

  const renameAttachment = (file, newName) => {
    const extension = path.extname(file.path).substring(1) || 'jpg';
    const newFileName = `${newName}.${extension}`;
    ApiService.post(`attachment/${file.id}/rename`, { filename: newFileName }).then(() => {
      toastr.success('Attachment Renamed', `${file.filename} -> ${newFileName}`);
      setAttachments(
        attachments.map(attachment => {
          if (attachment.id !== file.id) {
            return attachment;
          }

          return {
            ...attachment,
            filename: newFileName,
          };
        })
      );
    });
  };

  const updatePrimaryAttachment = fileId => {
    let last;
    setAttachments(
      attachments.map(attachment => {
        if (attachment.isPrimary) {
          last = attachment.id;
          return {
            ...attachment,
            isPrimary: false,
          };
        }

        if (attachment.id === fileId) {
          return {
            ...attachment,
            isPrimary: true,
          };
        }

        return attachment;
      })
    );

    return last;
  };

  const updatePublicAttachment = (attachmentId, value) => {
    setAttachments(
      attachments.map(attachment => {
        if (attachment.id === attachmentId) {
          return {
            ...attachment,
            isPublic: value,
          };
        }

        return attachment;
      })
    );
  };

  const onSetPrimary = file => {
    const last = updatePrimaryAttachment(file.id);
    return ApiService.put(`list/${itemName}/item/${encodeURIComponent(itemId)}/change-primary`, {
      attachmentId: file.id,
    })
      .then()
      .catch(() => {
        updatePrimaryAttachment(last);
      });
  };

  const onSetPublic = attachmentId => {
    const attachment = attachments.find(att => att.id === attachmentId);
    return ApiService.post(`attachment/${attachmentId}/change-public`, { isPublic: !attachment.isPublic }).then(
      response => {
        updatePublicAttachment(attachmentId, !attachment.isPublic);
      }
    );
  };

  const togglePublicFilter = () => {
    setOnlyPublic(c => !c);
  };

  const toggleImageFilter = () => {
    setOnlyImages(c => !c);
  };

  const toggleDocsFilter = () => {
    setOnlyDocs(c => !c);
  };

  const clearFilter = () => {
    setOnlyPublic(false);
    setOnlyImages(false);
    setOnlyDocs(false);
  };

  const showGallery = () => {
    setView('Gallery');
  };

  const showList = () => {
    setView('List');
  };

  useEffect(() => {
    if (setTitle) {
      setTitle(`Attachments (${itemId})`);
    }
    refreshList();
    fetchTags();
  }, []);

  useEffect(() => {
    if (onChange) {
      onChange(attachments);
    }

    setSelection(_.some(attachments, 'selected'));
  }, [attachments]);

  useEffect(() => {
    if (statusbarEl.current) {
      statusbarEl.current.setMessage(defaultStatusMessage());
    }
  }, [onlyTags, attachments, loading]);

  useEffect(() => {
    if (onlyImages) {
      setOnlyDocs(false);
    }
  }, [onlyImages]);

  useEffect(() => {
    if (onlyDocs) {
      setOnlyImages(false);
    }
  }, [onlyDocs]);

  const handleCameraComplete = images => {
    setCameraMode(false);

    if (!images || !images.length) {
      return;
    }

    // Loading temp thumbnails while they are being added as attachments
    setAttachments([
      ...attachments,
      ...images.map((image, index) => ({
        width: image.width,
        height: image.height,
        mimeType: image.mime,
        src: image.data,
        key: `new-${index}`,
        uploading: true,
      })),
    ]);

    ApiService.put('attachment', [
      {
        data: images.map(i => {
          i.url = i.data;
          return i;
        }),
      },
    ]).then(response => {
      addAttachments(response.data).then(() => {
        refreshList(true);
      });
    });
  };

  const handleCameraError = err => {
    console.error(err);
    setCameraMode(false);
  };

  const renderEmptyMessage = (message = 'Empty Attachments') => {
    const empty = (
      <div className="empty-wrapper">
        <div className="empty">
          {message}
          {!readOnly && (
            <>
              <br />
              Drop Files Here
            </>
          )}
        </div>
      </div>
    );

    if (readOnly) {
      return empty;
    }

    return (
      <Dropzone className="dropzone" onDrop={onDrop} ref={dropzoneEl}>
        {empty}
      </Dropzone>
    );
  };

  const renderAttachments = () => {
    if (loading) {
      return <LoadingPage />;
    }

    // Empty state
    if (attachments.length === 0) {
      return renderEmptyMessage();
    }

    const compiledAttachments = compileAttachments();

    if (!compiledAttachments.length) {
      return renderEmptyMessage('No Attachments Match Filter');
    }

    const renderImage = ({ ...props }) => {
      return (
        <SelectedImage
          {...props}
          renameAttachment={renameAttachment}
          onSetPrimary={onSetPrimary}
          onSetPublic={onSetPublic}
          onDetailTarget={handleDetailTarget}
          openAttachment={openAttachment}
          tags={tags}
          count={compiledAttachments.length}
          progress={progressMap[simpleHash(_.get(props, 'photo.filename') || _.get(props, 'photo.src'))]}
        />
      );
    };

    const gallery = <Gallery onClick={select} photos={compiledAttachments} renderImage={renderImage} />;
    const fileList = (
      <FileList
        files={attachments}
        onClick={select}
        readOnly={readOnly}
        removeFile={remove}
        reorderFiles={reorder}
        selectAttachment={selectAttachment}
      />
    );

    if (readOnly) {
      return view === 'Gallery' ? gallery : fileList;
    }

    return (
      <Dropzone disableClick className="dropzone" onDrop={onDrop} onScroll={handleScroll} ref={dropzoneEl}>
        {view === 'Gallery' ? gallery : fileList}
      </Dropzone>
    );
  };

  const renderAutoSelect = () => {
    return (
      <Button variant="secondary" onClick={selectAll} title="Select All" disabled={loading}>
        <i className="fas fa-check-double" />
      </Button>
    );
  };

  const renderSelectionToolbar = () => {
    return (
      <div className="selection-toolbar">
        <div className="selection-details">
          <div className="toolbar-button" title="Cancel" onClick={unSelectAll} style={{ cursor: 'pointer' }}>
            <i className="fal fa-times" />
          </div>
          {`${selectedCount()} selected`}
        </div>
        {!readOnly && (
          <div className="right-toolbar">
            <Dropdown alignRight className="tag-select no-caret" variant="secondary">
              <Dropdown.Toggle
                variant="secondary"
                id="dropdown-basic"
                disabled={tags.length === 0 || loading}
                title="Tag Selected"
              >
                <i className="fas fa-tags" />
                <i className="fas fa-plus-octagon secondary-icon" style={{ color: 'var(--green-5)' }} />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {tags.map(tag => {
                  return (
                    <Dropdown.Item key={tag.id} eventKey={tag.id} onSelect={addTagToSelected}>
                      {tag.label}
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown alignRight className="tag-select no-caret" variant="secondary">
              <Dropdown.Toggle
                variant="secondary"
                id="dropdown-basic"
                disabled={tags.length === 0 || loading}
                title="Remove Tag"
              >
                <i className="fas fa-tags" />
                <i className="fas fa-minus-octagon secondary-icon" style={{ color: 'var(--red-5)' }} />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {tags.map(tag => {
                  return (
                    <Dropdown.Item key={tag.id} eventKey={tag.id} onSelect={removeTagFromSelected}>
                      {tag.label}
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
            <div className="separator" />
            <Button variant="secondary" disabled={!selection} onClick={publicSelected} title="Make Public">
              <i className="fas fa-globe fa-fw" />
            </Button>
            <Button variant="secondary" disabled={!selection} onClick={privateSelected} title="Make Private">
              <i className="fas fa-key fa-fw" />
            </Button>
            <div className="batch-actions">
              <div className="separator" />
              {renderBatchActions()}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBatchActions = () => {
    return (
      <>
        <Button variant="secondary" disabled={!selection} onClick={linkSelected} title="Link Attachment">
          <i className="fas fa-link fa-fw" />
        </Button>
        <Button variant="secondary" disabled={!selection} onClick={downloadSelected} title="Download">
          <i className="fas fa-download fa-fw" />
        </Button>
        <Button variant="danger" disabled={!selection} onClick={removeSelected} title="Remove">
          <i className="fas fa-trash fa-fw" />
        </Button>
      </>
    );
  };

  const renderUploadButtons = () => {
    return (
      <>
        <Button
          variant="secondary"
          title="Google Image Search"
          onClick={() => {
            modal.open('info', {
              title: 'Image Search',
              headerClass: 'primary',
              hidePrint: true,
              message: (
                <GoogleImageSearch
                  query={getSearchQuery({ itemName, itemId, itemData })}
                  onSelected={handleImageSearchSelection}
                />
              ),
            });
          }}
        >
          <i className="fab fa-google" />
        </Button>
        {renderCameraButton()}
        <Button onClick={() => dropzoneEl.current && dropzoneEl.current.open()} variant="secondary">
          Upload
        </Button>
      </>
    );
  };

  const renderCameraButton = () => {
    if (!DetectRTC.hasWebcam) {
      return;
    }

    return (
      <Button onClick={() => setCameraMode(true)} variant="secondary" title="Snap Photo">
        <i className="fa fa-camera" />
      </Button>
    );
  };

  const renderFilterIcons = () => {
    return (
      <div>
        {onlyPublic && <i className="fas fa-globe fa-fw" title="Displaying Public Only" />}
        {onlyImages && <i className="fas fa-image-polaroid fa-fw" title="Displaying Images Only" />}
        {onlyDocs && <i className="fas fa-file-signature fa-fw" title="Displaying Docs Only" />}
      </div>
    );
  };

  const renderDefaultToolbar = () => {
    const hasFilter = onlyPublic || onlyImages || onlyDocs;

    return (
      <div className="default-toolbar">
        <div className="left-toolbar">
          <div
            className={`modal-button has-icon view-toggle ${view === 'Gallery' ? 'selected' : ''}`}
            onClick={showGallery}
            title="Gallery"
          >
            <i className="fas fa-image" />
            <span className="modal-button-label">Gallery</span>
          </div>
          <div
            className={`modal-button has-icon view-toggle ${view === 'List' ? 'selected' : ''}`}
            onClick={showList}
            title="List"
          >
            <i className="fas fa-list-alt" />
            <span className="modal-button-label">List</span>
          </div>
        </div>

        {!readOnly && (
          <div className="right-toolbar">
            <div className={classNames('gen-filter-ctrl', { 'has-filter': hasFilter })}>
              <Button onClick={clearFilter} variant="secondary" title="Clear Filter" className="clear-filter">
                <i className="fas fa-times" />
              </Button>
              <Dropdown alignRight className="filter-select no-caret" variant="secondary">
                <Dropdown.Toggle variant="secondary">
                  {hasFilter ? renderFilterIcons() : <i className="fas fa-filter fa-fw" />}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item key="images" eventKey="images" onSelect={toggleImageFilter}>
                    {onlyImages && <i className="fas fa-check fa-fw mr-1" style={{ color: 'var(--green-5)' }} />} Images
                  </Dropdown.Item>
                  <Dropdown.Item key="docs" eventKey="docs" onSelect={toggleDocsFilter}>
                    {onlyDocs && <i className="fas fa-check fa-fw mr-1" style={{ color: 'var(--green-5)' }} />}{' '}
                    Documents
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item key="public" eventKey="public" onSelect={togglePublicFilter}>
                    {onlyPublic && <i className="fas fa-check fa-fw mr-1" style={{ color: 'var(--green-5)' }} />} Public
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className={classNames('tag-filter-ctrl', { 'has-tags': onlyTags.length > 0 })}>
              <Button
                onClick={() => setOnlyTags([])}
                variant="secondary"
                title="Clear Tag Filter"
                className="clear-filter"
              >
                <i className="fas fa-times" />
              </Button>
              <Dropdown alignRight className="tag-select no-caret" variant="secondary">
                <Dropdown.Toggle variant="secondary" id="dropdown-basic" disabled={tags.length === 0 || loading}>
                  {onlyTags.length > 0 ? (
                    <>
                      <Tags tags={tags} attachmentTags={onlyTags} stacked />
                    </>
                  ) : (
                    <>
                      <i className="fas fa-tags" />
                    </>
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {tags.map(tag => {
                    return (
                      <Dropdown.Item key={tag.id} eventKey={tag.id} onSelect={filterTag}>
                        {onlyTags.includes(tag.id) && (
                          <i className="fas fa-check fa-fw mr-1" style={{ color: 'var(--green-5)' }} />
                        )}
                        {tag.label}
                      </Dropdown.Item>
                    );
                  })}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="separator" />
            {renderAutoSelect()}
            <Button
              onClick={handleTopShadeToggle}
              variant="secondary"
              disabled={loading}
              title="Toggle Shade (Gallery View)"
              className={classNames({ checked: showTopShade })}
            >
              <i className="fad fa-border-top" />
              <i className="fas fa-check-circle check" />
            </Button>
            <div className="upload-actions">
              <div className="separator" />
              {renderUploadButtons()}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (cameraMode) {
    return <BlazeCamera enableMultiShot={true} onComplete={handleCameraComplete} onError={handleCameraError} />;
  }

  return (
    <div
      className={classNames('modal-attachments', {
        'selection-mode': selection,
        'show-bottom-panel': !!detailTarget,
        'show-top-shade': showTopShade,
      })}
    >
      <div className="toolbar-wrapper">
        {selection ? renderSelectionToolbar() : renderDefaultToolbar()}
        <div className="clearfix" />
      </div>
      <div className="attachments">{renderAttachments()}</div>
      {!fetchingTags && (
        <BottomPanel
          attachment={attachments.find(a => a.id === detailTarget)}
          closePanel={() => setDetailTarget(null)}
          fetchTags={fetchTags}
          tags={tags}
          updateTags={updateTags}
          onSetPrimary={onSetPrimary}
          onSetPublic={onSetPublic}
        />
      )}
      <div className="bottom-toolbar">
        <div className="right-toolbar">{selection ? renderBatchActions() : renderUploadButtons()}</div>
      </div>

      <ModalStatusBar ref={statusbarEl} message={defaultStatusMessage()} />
    </div>
  );
};

ModalAttachments.propTypes = {
  itemId: PropTypes.string,
  itemName: PropTypes.string,
  itemData: PropTypes.object,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
};

export default ModalAttachments;
